import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  useEntryRules,
  useCreateEntryRule,
  useUpdateEntryRule,
  useSetMakerFlag,
  useHidePairFromOpener,
  useManualConfirmedPairs,
  useEntryPanic,
  useEntryPanicRecent
} from '../api/useEntry';
import { createSingleFlight } from '../api/ruleCreation';
import {
  buildCreateRulePayload,
  type CreateRuleInput
} from '../api/createRulePayload';
import { useOpenerGroupList } from '../api/useGroups';
import { useSubgroups } from '../api/useSubgroups';
import {
  GroupSelection,
  UNGROUPED_SENTINEL,
  pairMatchesGroup,
  readGroupSelection,
  resolveGroupSelection,
  writeGroupSelection
} from '../components/entry/groupFilter';
import {
  SubgroupSelection,
  pairMatchesSubgroup,
  readSubgroupSelection,
  writeSubgroupSelection
} from '../components/entry/subgroupFilter';
import { SubgroupRail } from '../components/entry/SubgroupRail';
import { matchesPairTitleSearch } from '../utils/pairSearch';
import { sortGroups } from '../utils/sortGroups';
import { useEntrySocket } from '../hooks/useEntrySocket';
import {
  buildGroupAccountIndex,
  useVenuePositionsStatusByScope
} from '../live/venuePositions';
import {
  getGroupAccountResolution,
  subscribeGroupAccountResolution
} from '../api/groupAccountResolution';
import { EntryTabByPath } from '../components/entry/EntryTabByPath';
import {
  isThresholdCreatable
} from '../components/entry/entryMath';
import { StopStartOpenerButtons } from '../components/entry/StopStartOpenerButtons';
import { HEADER_CONTROL_BTN_SX } from '../components/shared/headerButtonSx';
import { GroupPodControls } from '../components/entry/GroupPodControls';
import { GroupLiveSummaryStripLive } from '../components/bucket/GroupLiveSummaryStrip';
import { GroupPickerLanding } from '../components/entry/GroupPickerLanding';
import {
  ALL_GROUPS_VALUE,
  GroupSelect
} from '../components/entry/GroupSelect';
import { BootDomainGate } from '../components/system/BootDomainGate';
import { SoccerMatchDrawer } from '../components/entry/SoccerMatchDrawer';
import { useSoccerMatches } from '../api/useSoccerMatches';
import {
  EntryRule,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  MakerFlag
} from '../types/entry.types';

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

// `ALL_GROUPS_VALUE` is the Select-only value for the "All groups" option —
// the URL carries no `group` param for it (see readGroupSelection /
// writeGroupSelection). It lives with the Select that renders it.

export const EntryPage = () => {
  const createRuleMutation = useCreateEntryRule();
  const updateRuleMutation = useUpdateEntryRule();
  const setMakerFlagMutation = useSetMakerFlag();
  const hidePairMutation = useHidePairFromOpener();
  const panicMutation = useEntryPanic();
  const panicRecentMutation = useEntryPanicRecent();

  // Two side-specific tabs. Tab A = poly YES + kalshi NO; Tab B = poly NO
  // + kalshi YES. Default to A.
  const [tab, setTab] = useState<'a' | 'b'>('a');

  // Free-text search over pair titles. Matches `pairName`, `kalshiTitle`,
  // `polymarketTitle` (case-insensitive substring) so the operator can narrow
  // a long pair list to the one they care about. Empty / whitespace-only
  // string disables the filter. Mirrors the unwinder's UnwindingPage search.
  const [search, setSearch] = useState('');

  // Group filter — persisted in the URL as `?group=<id>` ('ungrouped'
  // sentinel for the no-group bucket; absent = All groups) so the
  // selection survives reloads, mirroring NotificationsPage's param
  // pattern. A dead id (group deleted elsewhere) resolves to All — but
  // ONLY once the groups query has resolved, never mid-load.
  const [searchParams, setSearchParams] = useSearchParams();
  // Read as the raw list value (rows + the moment they were asked for) rather
  // than the rows alone: the account guard below needs both, and they are only
  // trustworthy together.
  const { data: groupList, isSuccess: groupsResolved } = useOpenerGroupList();
  const groups = groupList?.groups;
  const urlSelection = useMemo(
    () => readGroupSelection(searchParams),
    [searchParams]
  );
  const groupSelection = useMemo(
    () => resolveGroupSelection(urlSelection, groups, groupsResolved),
    [urlSelection, groups, groupsResolved]
  );
  const sortedGroups = useMemo(() => sortGroups(groups), [groups]);

  // Pairs are read group-scoped for the same reason the rules below are: a
  // concrete group hits its OWN feed pod for just that group's pairs instead
  // of blocking the whole table on the ~3.6k-pair (~10 MB) universe served
  // unscoped. Declared AFTER `groupSelection` because it consumes it.
  const { data: pairsData, isLoading } =
    useManualConfirmedPairs(groupSelection);

  // Rules are read group-scoped: a concrete group hits its own feed pod for
  // just that group's rules (+ the legacy null bucket); 'all'/'ungrouped'
  // stay on the core base with the full list. Scoping the read is what keeps
  // the core opener pod from being starved by an unfiltered ~9.5k-doc fetch.
  const { data: rules } = useEntryRules({ groupSelection });

  // Subgroup filter — nested inside the selected group, persisted as
  // `?subgroup=<id>` ('unassigned' sentinel). Only meaningful when a single
  // group is selected; the param is force-cleared on a group switch (see
  // handleGroupChange) so a subgroup never leaks across groups.
  const subgroupSelection = useMemo<SubgroupSelection>(
    () => readSubgroupSelection(searchParams),
    [searchParams]
  );
  // Subgroups for the active group — shares SubgroupRail's react-query cache,
  // so this adds no extra fetch. Resolves the selected subgroup (dead-id
  // fallback) so the bulk-params bar only enters subgroup scope for a live
  // subgroup, and quotes its display name in "Apply to {subgroup}".
  const activeGroupId =
    groupSelection.kind === 'group' ? groupSelection.groupId : null;
  const { data: subgroupsData, isSuccess: subgroupsResolved } =
    useSubgroups(activeGroupId);
  const activeSubgroup = useMemo(() => {
    if (subgroupSelection.kind !== 'subgroup') return null;
    if (!subgroupsResolved) return null;
    return (
      subgroupsData?.subgroups.find(
        s => s._id === subgroupSelection.subgroupId
      ) ?? null
    );
  }, [subgroupSelection, subgroupsData, subgroupsResolved]);
  const handleSubgroupChange = useCallback(
    (selection: SubgroupSelection) => {
      setSearchParams(writeSubgroupSelection(searchParams, selection), {
        replace: true
      });
    },
    [searchParams, setSearchParams]
  );

  // Socket target follows the group selection: a selected group with a
  // Ready feed pod streams from the per-group feed; everything else (and
  // any feed connect_error) uses the core gateway.
  const { isConnected, store, groupCostStore, venueFrame } =
    useEntrySocket(groupSelection);

  const handleGroupChange = useCallback(
    (value: string) => {
      const selection: GroupSelection =
        value === ALL_GROUPS_VALUE
          ? { kind: 'all' }
          : value === UNGROUPED_SENTINEL
            ? { kind: 'ungrouped' }
            : { kind: 'group', groupId: value };
      // CRITICAL: a subgroup is meaningless outside its parent group, so the
      // subgroup param is cleared on every group switch.
      const withGroup = writeGroupSelection(searchParams, selection);
      const cleared = writeSubgroupSelection(withGroup, { kind: 'all' });
      setSearchParams(cleared, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Clean the dead id out of the URL once the fallback-to-All resolves so
  // a reload / share doesn't carry the stale param around.
  useEffect(() => {
    if (urlSelection.kind === 'group' && groupSelection.kind === 'all') {
      const withGroup = writeGroupSelection(searchParams, { kind: 'all' });
      setSearchParams(writeSubgroupSelection(withGroup, { kind: 'all' }), {
        replace: true
      });
    }
  }, [urlSelection, groupSelection, searchParams, setSearchParams]);

  // Active group object — feeds GroupPodControls the default-config state
  // (`defaultConfigSavedAt` / `pairCount`) for the Save/Reset default buttons.
  const activeGroup = useMemo(
    () =>
      groupSelection.kind === 'group'
        ? (groups?.find(g => g._id === groupSelection.groupId) ?? null)
        : null,
    [groupSelection, groups]
  );

  // Soccer match drawer — the read model is fetched continuously (not just
  // while the drawer is open) so the "Matches" button's visibility gate
  // (≥1 soccer match in the group) doesn't lag a group switch.
  const [matchesDrawerOpen, setMatchesDrawerOpen] = useState(false);
  const { data: soccerMatchesData } = useSoccerMatches(
    activeGroupId,
    groupSelection.kind === 'group'
  );
  const soccerMatches = soccerMatchesData?.matches ?? [];

  // WHICH account each ROW trades under. Resolved per group rather than per
  // page: "All groups" and "Ungrouped" show pairs from `testAccount` groups
  // alongside main-account ones, and the main-account REST numbers must never
  // stand in for either an unresolved group or a test one.
  const { marks: groupAccountMarks } = useSyncExternalStore(
    subscribeGroupAccountResolution,
    getGroupAccountResolution
  );
  const groupAccounts = useMemo(
    () => buildGroupAccountIndex(groupList, groupAccountMarks),
    [groupList, groupAccountMarks]
  );

  // The socket state is passed in because it is the ONE thing that separates
  // "the group's feed pod stopped sweeping" from "this page lost the socket
  // carrying its heartbeat" — without it a reconnect blanks the ACT / YES / NO
  // cells of every test-account group and they flip back on the next frame.
  const venueStatusByScope = useVenuePositionsStatusByScope(
    venueFrame,
    !isConnected
  );

  // Display name for the active filter — feeds the tab's bulk-bar labels.
  const groupLabel = useMemo(() => {
    if (groupSelection.kind === 'ungrouped') return 'Ungrouped';
    if (groupSelection.kind === 'group') {
      return (
        groups?.find(g => g._id === groupSelection.groupId)?.name ?? null
      );
    }
    return null;
  }, [groupSelection, groups]);

  // Select value mirror of the resolved selection ('all' sentinel for the
  // param-less All-groups state).
  const groupSelectValue =
    groupSelection.kind === 'all'
      ? ALL_GROUPS_VALUE
      : groupSelection.kind === 'ungrouped'
        ? UNGROUPED_SENTINEL
        : groupSelection.groupId;

  const pairs: ManualConfirmedPair[] = useMemo(() => pairsData ?? [], [pairsData]);

  // Group + search filters, composed: the group predicate narrows first,
  // then the shared case-insensitive title search (`matchesPairTitleSearch`
  // — same matcher the bucket page uses). Either filter alone is a no-op
  // when inactive ("All groups" / empty query). Applied before the list
  // reaches the tab so both tabs share the same filters. `groupedPairs`
  // (pre-search) is kept separately and drilled into the tab: it is what a
  // server-side "All ON/OFF" actually flips (confirm-dialog count) and the
  // source set for the bulk-params bar's group-scoped rule ids.
  const groupedPairs = useMemo(
    () =>
      groupSelection.kind === 'all'
        ? pairs
        : pairs.filter(p => pairMatchesGroup(p, groupSelection)),
    [pairs, groupSelection]
  );
  // Within a selected group, a subgroup selection narrows the table + bulk
  // scope further. Subgroups only exist inside a single group, so the
  // narrowing is a no-op unless one group is selected. `groupedPairs` stays
  // the full group set — the subgroup rail's counts + assign panel need it.
  const subgroupScopedPairs = useMemo(() => {
    if (groupSelection.kind !== 'group') return groupedPairs;
    return groupedPairs.filter(p =>
      pairMatchesSubgroup(p, subgroupSelection)
    );
  }, [groupedPairs, groupSelection, subgroupSelection]);
  const visiblePairs = useMemo(() => {
    if (!search.trim()) return subgroupScopedPairs;
    return subgroupScopedPairs.filter(p => matchesPairTitleSearch(p, search));
  }, [subgroupScopedPairs, search]);


  const sumRules = useMemo(
    () => (rules ?? []).filter(r => r.ruleType === 'sum_of_sides'),
    [rules]
  );

  // All rules keyed by pairId — used by the row's status indicator so it
  // works regardless of the active tab.
  const anyRulesByPairId = useMemo(() => {
    const map = new Map<string, EntryRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      const existing = map.get(rule.pairId);
      if (!existing) {
        map.set(rule.pairId, rule);
        continue;
      }
      const incomingActive = rule.status === 'active';
      const existingActive = existing.status === 'active';
      if (incomingActive && !existingActive) {
        map.set(rule.pairId, rule);
      }
    }
    return map;
  }, [rules]);

  // Single-flight create per pair: every rule-less gesture (param ✓/Enter,
  // maker switch, TT switch) funnels through `handleCreateSumRule`, so the
  // map below guarantees at most ONE POST /rules in flight per pair. A
  // second gesture landing before the refetch joins the in-flight create's
  // promise and PATCHes the resolved rule instead of creating a duplicate.
  const inFlightCreates = useRef(createSingleFlight<EntryRule>());

  // The page no longer decides WHAT goes in the body — it only decides WHERE it
  // goes. The whitelist that used to live here is why four fields the operator
  // configured never reached the POST: adding a column meant remembering to add
  // a name to a list nobody was looking at.
  const handleCreateSumRule = useCallback(
    async (input: CreateRuleInput): Promise<EntryRule | null> => {
      if (!input.pair._id) return null;
      const dto = buildCreateRulePayload(input);
      // Hard-cap layer 3 (the last FE backstop before POST /rules): sub-$1
      // unless the pair opted into "> $1", then <= 2.0 coarse (backend enforces
      // the precise per-mode bound). Pure + unit-tested — see isThresholdCreatable.
      if (
        !isThresholdCreatable(
          dto.thresholdSum as number,
          dto.allowThresholdAboveOne === true
        )
      ) {
        return null;
      }
      return inFlightCreates.current.ensure(input.pair._id, async () =>
        createRuleMutation.mutateAsync(dto)
      );
    },
    [createRuleMutation]
  );

  const handleSetMakerFlag = useCallback(
    async (
      rule: EntryRule,
      flag: MakerFlag,
      value: boolean,
      clearOthers?: MakerFlag[],
    ) => {
      await setMakerFlagMutation.mutateAsync({
        id: rule._id,
        flag,
        value,
        clearOthers,
      });
    },
    [setMakerFlagMutation],
  );

  const handleHidePair = useCallback(
    async (pair: ManualConfirmedPair) => {
      if (!pair._id) return;
      await hidePairMutation.mutateAsync(pair._id);
    },
    [hidePairMutation],
  );

  // Returns the SERVER'S rule, not a boolean.
  //
  // The mutation already resolves with it and this used to throw it away, which
  // left the row with nothing to confirm its draft against except the list
  // query — and between the ack and that query's refetch the cache still serves
  // the OLD value, so the row would flash backwards. `null` on failure, so a
  // caller can tell "rejected" from "saved".
  const handleUpdateRule = useCallback(
    async (rule: EntryRule, dto: UpdateEntryRuleDto): Promise<EntryRule | null> => {
      try {
        return await updateRuleMutation.mutateAsync({ id: rule._id, dto });
      } catch {
        return null;
      }
    },
    [updateRuleMutation]
  );

  return (
    <Box sx={{ px: 2, pt: 0.5, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* Row 1 — title + tabs on left, global controls on right */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.5, flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" sx={{ color: 'common.white' }}>
            Opening
          </Typography>
          <Tabs
            value={tab}
            onChange={(_, v: 'a' | 'b') => setTab(v)}
            sx={{
              minHeight: 28,
              '& .MuiTab-root': { minHeight: 28, py: 0.25, fontSize: '0.75rem' }
            }}
          >
            <Tab value="a" label="Polymarket YES + Kalshi NO" />
            <Tab value="b" label="Polymarket NO + Kalshi YES" />
          </Tabs>
        </Stack>

        {/* Control-plane strip. The buttons themselves never wrap their labels
            (HEADER_CONTROL_BTN_SX) — on a genuinely narrow screen the GROUP
            wraps instead, which keeps every button one line tall. */}
        <Stack
          direction="row"
          spacing={0.4}
          alignItems="center"
          useFlexGap
          sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}
        >
          {!isConnected && (
            <Chip
              icon={<FiberManualRecordIcon sx={{ fontSize: '0.6rem !important' }} />}
              label="Disconnected"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
          <StopStartOpenerButtons />
          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => panicRecentMutation.mutate()}
            sx={HEADER_CONTROL_BTN_SX}
            disabled={panicRecentMutation.isLoading}
            title="Cancel buys + pause rules active in the last 24h. Orders on unrelated markets are NOT touched."
          >
            {panicRecentMutation.isLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              'PANIC 24H'
            )}
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<WarningAmberIcon />}
            onClick={() => panicMutation.mutate()}
            sx={HEADER_CONTROL_BTN_SX}
            disabled={panicMutation.isLoading}
          >
            {panicMutation.isLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              'PANIC'
            )}
          </Button>
        </Stack>
      </Stack>

      {/* Row 2 — search, group selector, group pod controls on left; match counter */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5, flexShrink: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          useFlexGap
          sx={{ flexWrap: 'wrap', minWidth: 0 }}
        >
          <TextField
            size="small"
            placeholder="Search by title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: 240,
              '& .MuiOutlinedInput-root': { fontSize: '0.75rem', height: 28 },
              '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
            }}
            inputProps={{ 'aria-label': 'Search pairs by title' }}
          />
          <GroupSelect
            value={groupSelectValue}
            onChange={handleGroupChange}
            groups={sortedGroups}
            ungroupedValue={UNGROUPED_SENTINEL}
            unresolvedGroupId={
              groupSelection.kind === 'group' &&
              !sortedGroups.some(g => g._id === groupSelection.groupId)
                ? groupSelection.groupId
                : null
            }
          />
          {groupSelection.kind === 'group' && (
            <GroupPodControls
              groupId={groupSelection.groupId}
              groupName={groupLabel}
              group={activeGroup}
            />
          )}
          {groupSelection.kind === 'group' && soccerMatches.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setMatchesDrawerOpen(true)}
              sx={HEADER_CONTROL_BTN_SX}
            >
              Matches ({soccerMatches.length})
            </Button>
          )}
        </Stack>
        {search.trim() && (
          <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
            {visiblePairs.length}/{subgroupScopedPairs.length} matched
          </Typography>
        )}
      </Stack>

      {/* Live trading summary for the selected group — descalce + contracts
          (real-time socket) and nominal/PnL (money) — watched while operating. */}
      {groupSelection.kind === 'group' && activeGroup && (
        <GroupLiveSummaryStripLive
          group={activeGroup}
          groupCostStore={groupCostStore}
        />
      )}

      {/* Row 3 — subgroup rail, nested inside the selected group only. */}
      {groupSelection.kind === 'group' && (
        <SubgroupRail
          groupId={groupSelection.groupId}
          path={tab}
          groupedPairs={groupedPairs}
          selection={subgroupSelection}
          onSelectionChange={handleSubgroupChange}
        />
      )}

      {/* Rule CRUD is served by the position-opener-node pod, which scales to
          0 with the opener domain — gate ONLY this region so the control-plane
          strip above (the opener ON/OFF control, its ⋮ escape hatches, the
          group pod controls and PANIC) stays reachable. */}
      <Box sx={{ flex: 3, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <BootDomainGate domain="opener">
        {groupSelection.kind === 'all' ? (
          // The 'all' view never loads rules (the query is disabled), so the
          // table is NOT rendered here — a landing lets the operator pick a
          // group and open it group-scoped. This is also where a stale
          // `?group=<deadId>` resolves to, so it must render rather than flash
          // an empty table.
          <GroupPickerLanding
            groups={sortedGroups}
            onSelectGroup={handleGroupChange}
            onSelectUngrouped={() => handleGroupChange(UNGROUPED_SENTINEL)}
          />
        ) : isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !isConnected && pairs.length === 0 ? (
          // Only show the placeholder when there is genuinely nothing to
          // render (never connected, no cached pairs). A TRANSIENT WS drop
          // (socket.connected flaps false→true on every reconnect) must
          // NOT tear down the table: the pair list is react-query-cached
          // and the live-state Map persists in useEntrySocket, so the rows
          // stay put. Unmounting here was what made the whole list vanish
          // for ~1-2s and then reappear reordered (fresh mount reset the
          // sort + a new opening_state snapshot re-sorted). The header
          // "Live / Disconnected" chip already signals connection state.
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
              Disconnected — waiting for opener backend.
            </Typography>
          </Box>
        ) : (
          <EntryTabByPath
            path={tab}
            pairs={visiblePairs}
            groupSelection={groupSelection}
            groupLabel={groupLabel}
            subgroupId={activeSubgroup?._id ?? null}
            subgroupLabel={activeSubgroup?.name ?? null}
            groupedPairs={subgroupScopedPairs}
            rules={sumRules}
            anyRulesByPairId={anyRulesByPairId}
            store={store}
            venueStatusByScope={venueStatusByScope}
            groupAccounts={groupAccounts}
            onCreateRule={handleCreateSumRule}
            onSetMakerFlag={handleSetMakerFlag}
            onUpdateRule={handleUpdateRule}
            onHidePair={handleHidePair}
          />
        )}
        </BootDomainGate>
      </Box>

      {groupSelection.kind === 'group' && (
        <SoccerMatchDrawer
          open={matchesDrawerOpen}
          onClose={() => setMatchesDrawerOpen(false)}
          groupId={groupSelection.groupId}
          groupLabel={groupLabel}
          tab={tab}
          rulesByPairId={anyRulesByPairId}
        />
      )}
    </Box>
  );
};
